import type { MouseEvent } from 'react'
import { useCallback, useContext, useMemo } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import Clickable from 'components/Common/Clickable'
import MaldivesOnePointPromoModal from 'components/MaldivesDirect/OnePointPromo/MaldivesOnePointPromoModal'
import ModalContext from 'contexts/ModalContext'
import MessagingLabel from 'LuxKit/Label/MessagingLabel'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'

const fireIcon = <SolidFireIcon />

const SaleLabel = styled(MessagingLabel)`
  padding-inline: ${rem(4)};
  white-space: nowrap;
  flex-shrink: 0;
`

export const oneWayFlightMessages = defineMessages({
  depart: {
    id: 'ZX_UlaCD2tXMRjlfxk05',
    defaultMessage: 'Depart',
    description: 'Date-column label for the included or separately booked outbound flight in a one-way Maldives charter calendar option.',
  },
  return: {
    id: '4C3hdm_eygt25FMdVXxT',
    defaultMessage: 'Return',
    description: 'Date-column label for the included or separately booked inbound flight in a one-way Maldives charter calendar option.',
  },
  mustBeBookedSeparately: {
    id: 'EYrV-g5Rxag7WGoJuppH',
    defaultMessage: 'Must be booked separately',
    description: 'Replacement text for the unavailable flight date in a one-way Maldives charter calendar option.',
  },
  sale: {
    id: 'C1pEtYUCcfwHwEBvJ-0w',
    defaultMessage: 'One-way flight sale',
    description: 'Promotional badge shown on Maldives charter calendar options that include only one international flight leg.',
  },
})

interface Props {
  departureDate: string | undefined
}

export function OneWayFlightSaleLabel({ departureDate }: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const onLearnMore = useCallback(async(event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    event.preventDefault()
    event.stopPropagation()
    const trigger = event.currentTarget
    await showModal(<MaldivesOnePointPromoModal />)
    if (trigger.isConnected) trigger.focus()
  }, [showModal])
  const infoButton = useMemo(() => (
    <Clickable onClick={onLearnMore} aria-label={intl.formatMessage(oneWayFlightMessages.sale)} aria-haspopup="dialog">
      <LineInfoCircleIcon size={16} />
    </Clickable>
  ), [intl, onLearnMore])

  if (departureDate !== '2026-11-03') return null

  return (
    <SaleLabel startIcon={fireIcon} endIcon={infoButton} variant="urgency">
      {intl.formatMessage(oneWayFlightMessages.sale)}
    </SaleLabel>
  )
}
